/**
 * Transfer wizard page hook — composes routing, form state, lookups, and workflow handlers.
 */
import { useEffect } from 'react'
import { useAppDispatch } from '@/app/hooks'
import { useUserRole } from '@/hooks/useUserRole'
import { getStates } from '@/lib/api/statesService'
import { defaultTransferFormValues } from '../utils/transferFormUtils'
import { useTransferProcessCoreState } from './useTransferProcessCoreState'
import { useTransferProcessDialogs } from './useTransferProcessDialogs'
import { useTransferProcessForm } from './useTransferProcessForm'
import { useTransferProcessLookups } from './useTransferProcessLookups'
import { useTransferProcessRouting } from './useTransferProcessRouting'
import { useTransferProcessWorkflow } from './useTransferProcessWorkflow'

export function useTransferProcessPage() {
  const dispatch = useAppDispatch()
  const { user, hasPolicy } = useUserRole()
  const routing = useTransferProcessRouting()
  const dialogs = useTransferProcessDialogs(routing.processNotesIdParam, routing.processIdParam)
  const form = useTransferProcessForm()
  const core = useTransferProcessCoreState()

  const lookups = useTransferProcessLookups({
    fragment: routing.fragment,
    entityId: routing.entityId,
    fkCompanyId: form.fkCompanyId,
    core,
  })

  const workflow = useTransferProcessWorkflow({
    dispatch,
    routing,
    dialogs,
    form,
    core,
    lookups,
  })

  // Destructure the stable useState setters so the effects below don't depend on the
  // per-render `core` object literal (which would re-run them every render).
  const { setReadOnlyControl, setStates } = core

  useEffect(() => {
    const cid = user?.current_role?.company_id
    setReadOnlyControl(cid != null && Number(cid) > 0 ? ['company'] : [])
  }, [user, setReadOnlyControl])

  useEffect(() => {
    getStates().then(setStates).catch(() => setStates([]))
  }, [setStates])

  return {
    dispatch,
    navigate: routing.navigate,
    hasPolicy,
    fragment: routing.fragment,
    pageHeaderTitle: routing.pageHeaderTitle,
    entityId: routing.entityId,
    entityLoadStatus: core.entityLoadStatus,
    processing: core.processing,
    states: core.states,
    propertyData: core.propertyData,
    loadNewCompany: core.loadNewCompany,
    companyList: core.companyList,
    companyListOld: core.companyListOld,
    propertyList: core.propertyList,
    companyOptionsLoading: core.companyOptionsLoading,
    companyOldOptionsLoading: core.companyOldOptionsLoading,
    propertyOptionsLoading: core.propertyOptionsLoading,
    allProperty: core.allProperty,
    selectedTab: core.selectedTab,
    setSelectedTab: core.setSelectedTab,
    selectedSubTab: core.selectedSubTab,
    setSelectedSubTab: core.setSelectedSubTab,
    staffList: core.staffList,
    removestaffList: core.removestaffList,
    transferstaffList: core.transferstaffList,
    addedstaffList: core.addedstaffList,
    commitstaffList: core.commitstaffList,
    selectedRemoveRoleList: core.selectedRemoveRoleList,
    selectedTransferRoleList: core.selectedTransferRoleList,
    selectedAddRoleList: core.selectedAddRoleList,
    residents: core.residents,
    applications: core.applications,
    residentTablesRevision: core.residentTablesRevision,
    pageOptions: core.pageOptions,
    pageOptionsApplication: core.pageOptionsApplication,
    urlParamsRef: workflow.urlParamsRef,
    urlParamsAppRef: workflow.urlParamsAppRef,
    selectedResidentList: core.selectedResidentList,
    setSelectedResidentList: core.setSelectedResidentList,
    selectedApplicationList: core.selectedApplicationList,
    setSelectedApplicationList: core.setSelectedApplicationList,
    listedmanagementList: core.listedmanagementList,
    infoDone: core.infoDone,
    empDone: core.empDone,
    residentDone: core.residentDone,
    commitDone: core.commitDone,
    verifyDone: core.verifyDone,
    primaryContactSet: core.primaryContactSet,
    domains: core.domains,
    newdomains: core.newdomains,
    newPropertyLabel: core.newPropertyLabel,
    newPropertySave: core.newPropertySave,
    residentcount: core.residentcount,
    applicantcount: core.applicantcount,
    process_id_notes: dialogs.process_id_notes,
    transferNotesRefreshKey: dialogs.transferNotesRefreshKey,
    bumpTransferLogNotesRefresh: dialogs.bumpTransferLogNotesRefresh,
    progressData: core.progressData,
    confirmPaymentClone: dialogs.confirmPaymentClone,
    setConfirmPaymentClone: dialogs.setConfirmPaymentClone,
    confirmInactiveListed: dialogs.confirmInactiveListed,
    setConfirmInactiveListed: dialogs.setConfirmInactiveListed,
    staffSelectionOpen: dialogs.staffSelectionOpen,
    setStaffSelectionOpen: dialogs.setStaffSelectionOpen,
    addEmployeeModalOpen: dialogs.addEmployeeModalOpen,
    addEmployeeModalCtx: dialogs.addEmployeeModalCtx,
    closeAddEmployeeModal: dialogs.closeAddEmployeeModal,
    userEditModalUserId: dialogs.userEditModalUserId,
    setUserEditModalUserId: dialogs.setUserEditModalUserId,
    flowConfirm: dialogs.flowConfirm,
    handleFlowConfirmPrimary: dialogs.handleFlowConfirmPrimary,
    handleFlowConfirmSecondary: dialogs.handleFlowConfirmSecondary,
    readOnlyControl: core.readOnlyControl,
    register: form.register,
    watch: form.watch,
    reset: form.reset,
    setValue: form.setValue,
    getValues: form.getValues,
    formState: form.formState,
    trigger: form.trigger,
    setValueValidated: form.setValueValidated,
    fkCompanyId: form.fkCompanyId,
    paymentMethod: form.paymentMethod,
    paymentMethodNewProp: form.paymentMethodNewProp,
    payeeName: form.payeeName,
    payeeNameNew: form.payeeNameNew,
    newPropertyName: form.newPropertyName,
    loadCompany: lookups.loadCompany,
    debouncedLoadCompanySearch: lookups.debouncedLoadCompanySearch,
    loadCompanyOld: lookups.loadCompanyOld,
    loadProperty: lookups.loadProperty,
    searchProperty: lookups.searchProperty,
    loadStaffs: workflow.loadStaffs,
    loadNewStaffs: workflow.loadNewStaffs,
    loadAddedStaffs: workflow.loadAddedStaffs,
    loadResidents: workflow.loadResidents,
    loadApplications: workflow.loadApplications,
    loadListedManagement: workflow.loadListedManagement,
    openAddEmployeeModal: workflow.openAddEmployeeModal,
    handleCompanySelected: workflow.handleCompanySelected,
    applySetPropertyInfo: workflow.applySetPropertyInfo,
    maybeInactiveListed: workflow.maybeInactiveListed,
    updateStaffandResident: workflow.updateStaffandResident,
    saveTransferProgress: workflow.saveTransferProgress,
    draftTransferStaff: workflow.draftTransferStaff,
    selectAllTransfer: workflow.selectAllTransfer,
    userSelectedTransfer: workflow.userSelectedTransfer,
    selectAllResident: workflow.selectAllResident,
    selectAllApplication: workflow.selectAllApplication,
    transferResident: workflow.transferResident,
    completeStaffTransfer: workflow.completeStaffTransfer,
    completeSplitPropertyTransfer: workflow.completeSplitPropertyTransfer,
    completeVerify: workflow.completeVerify,
    onPrimaryContactSave: workflow.onPrimaryContactSave,
    showPayments: workflow.showPayments,
    showEmployeeTab: workflow.showEmployeeTab,
    tabOrder: workflow.tabOrder,
    currentTabIndex: workflow.currentTabIndex,
    nextDisabled: workflow.nextDisabled,
    transferEntityTabs: workflow.transferEntityTabs,
    previousTab: workflow.previousTab,
    nextTab: workflow.nextTab,
    defaultTransferFormValues,
  }
}

export type TransferProcessPageContext = ReturnType<typeof useTransferProcessPage>
